"use client"
import { useState, useEffect } from "react"
import { Link } from 'react-router-dom'
import slide1 from '../images/slide1.jpg';
import slide2 from '../images/slide2.jpg';
import slide3 from '../images/slide3.jpg';
import slide4 from '../images/slide4.jpg';
import slide5 from '../images/slide5.jpg';
import slide6 from '../images/slide6.webp';

import { ChevronDown, MessageCircle, X } from "lucide-react"

export default function RegistrationPolicyPage() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const constructionImages = [
    slide1,
    slide2,
    slide3,
    slide4,
    slide5,
    slide6
  ]

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % constructionImages.length)
    }, 4000)
    return () => clearInterval(slideInterval)
  }, [constructionImages.length])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <div className="bg-white text-gray-900 overflow-x-hidden">
      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/20 backdrop-blur-2xl border-b border-white/10 shadow-xl"
            : "bg-gray-900/30 backdrop-blur-xl text-white border-b border-white/5"
        }`}
      >
        <div className="container mx-auto flex justify-between items-center px-6 py-4">
          <div
            className={`text-2xl font-bold transition-all duration-500 ${isScrolled ? "text-[#06b6d4]" : "text-white"}`}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#00d4ff] via-[#5b21b6] to-[#ec4899] bg-clip-text text-transparent animate-gradient-x bg-[length:200%_100%]">
                StructuralGuru
              </span>
              <div className="absolute -inset-3 bg-gradient-to-r from-[#00d4ff]/20 via-[#5b21b6]/20 to-[#ec4899]/20 rounded-2xl blur-2xl opacity-50 animate-pulse"></div>
              <div
                className="absolute -inset-4 bg-gradient-to-r from-[#00d4ff]/15 via-[#5b21b6]/15 to-[#ec4899]/15 rounded-3xl blur-3xl opacity-30 animate-pulse"
                style={{ animationDelay: "1s" }}
              ></div>
            </span>
          </div>
          <nav className="hidden md:flex space-x-8">
            {[
              { name: "HOME", path: "/" },
              { name: "ABOUT US", path: "/about" },
              { name: "SERVICES", path: "/services" },
              { name: "OUR ENGG PROJECTS", path: "/projects" },
            ].map(({ name, path }) => (
              <Link
                key={name}
                to={path}
                className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                  isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                } ${
                  isScrolled
                    ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                    : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
              >
                {name}
              </Link>
            ))}
            <div className="relative">
              <button
                onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
                className={`flex items-center space-x-1 font-medium transition-all duration-300 hover:scale-105 ${
                  isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                } ${
                  isScrolled
                    ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                    : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                } rounded-lg px-2 py-1`}
              >
                <span>MORE</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${isMoreDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`absolute top-full right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/20 overflow-hidden transition-all duration-300 ${
                  isMoreDropdownOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible"
                }`}
              >
                <Link
                  to="/privacy"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/quality"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                >
                  Quality Policy
                </Link>
                <Link
                  to="/terms"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                >
                  Terms & Conditions
                </Link>
              </div>
            </div>
          </nav>
          {/* Mobile menu button */}
          <button className="md:hidden">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Registration Policy Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        {/* Construction Images Slideshow Background */}
        <div className="absolute inset-0">
          {constructionImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-20" : "opacity-0"
              }`}
            >
              <img
                src={image || "/placeholder.svg"}
                alt={`Construction ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          {/* Overlay gradients */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f766e]/30 via-transparent to-[#7c3aed]/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f766e]/40 via-transparent to-[#0f766e]/20"></div>
        </div>

        {/* Background gradient overlay */}
        {/* <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/80"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f766e]/10 via-transparent to-[#0f766e]/10"></div> */}

        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="animate-fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold mb-16 text-center relative">
              <span className="bg-gradient-to-r from-[#0f766e] via-[#7c3aed] to-[#0f766e] bg-clip-text text-transparent">
                Registration Policy
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/20 to-[#3b82f6]/20 blur-xl animate-pulse"></div>
            </h1>
          </div>

          <div className="group bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-gray-200/50 hover:border-[#06b6d4]/50 relative overflow-hidden animate-fade-in-up delay-200">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#06b6d4]/5 via-transparent to-[#7c3aed]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            {/* Glowing border effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#06b6d4]/20 to-[#7c3aed]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>

            <div className="relative z-10">
              <p className="mb-6 text-lg leading-relaxed text-gray-700 group-hover:text-gray-800 transition-colors duration-300">
                This registration policy outlines the rules and procedures for enrolling in training courses and
                services provided by{" "}
                <strong className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
                  StructuralGuru
                </strong>
                . All students are expected to read and accept these terms during the registration process.
              </p>

              <PolicySection
                title="1. Registration Process"
                items={[
                  <>
                    Students must fill out the official{" "}
                    <Link
                      key="registration-form"
                      to="/register"
                      className="text-[#0f766e] hover:text-[#7c3aed] underline transition-colors duration-300 font-medium"
                    >
                      registration form
                    </Link>{" "}
                    with accurate details.
                  </>,
                  "Course enrollment is confirmed only after successful fee payment.",
                  "Registrants will receive confirmation via email/WhatsApp within 48 hours.",
                ]}
              />

              <PolicySection
                title="2. Eligibility Criteria"
                items={[
                  "Applicants must be at least 16 years old or in their final year of diploma/UG engineering.",
                  "Prior experience is not required for beginner courses.",
                ]}
              />

              <PolicySection
                title="3. Batch Allocation"
                items={[
                  "Batches will be assigned based on availability and student preference.",
                  "Batch change requests must be sent at least 2 days in advance.",
                ]}
              />

              <PolicySection
                title="4. Fee Policies"
                items={[
                  "All fees must be paid in full before training begins.",
                  <>
                    Refer to our{" "}
                    <Link
                      key="cancellation-policy"
                      to="/cancellation"
                      className="text-[#0f766e] hover:text-[#7c3aed] underline transition-colors duration-300 font-medium"
                    >
                      Cancellation Policy
                    </Link>{" "}
                    for refund terms.
                  </>,
                  "Partial payments may be allowed for long-term programs (with prior approval).",
                ]}
              />

              <PolicySection
                title="5. Support"
                content={
                  <p className="text-gray-700 group-hover:text-gray-800 transition-colors duration-300 leading-relaxed">
                    For any issues related to registration, please contact us at{" "}
                    <strong className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
                      structuralguruofficial@gmail.com
                    </strong>{" "}
                    or call{" "}
                    <strong className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
                      +91 96291 21300
                    </strong>
                    .
                  </p>
                }
              />
            </div>

            {/* Hover effect particles */}
            <div className="absolute top-4 right-4 w-2 h-2 bg-[#06b6d4] rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:animate-ping"></div>
            <div
              className="absolute bottom-4 left-4 w-1 h-1 bg-[#7c3aed] rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:animate-pulse"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-64 h-64 bg-gradient-to-r from-[#06b6d4]/10 to-[#7c3aed]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-gradient-to-l from-[#7c3aed]/10 to-[#06b6d4]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        <div className="relative z-10 py-12">
          <div className="container mx-auto px-6">
            {/* Main Footer Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[
                { name: "Home", to: "/" },
                { name: "PSDCT Live Mode", to: "/psdct" },
                { name: "Term & Conditions", to: "/terms" },
                { name: "Quick Pay Any Fees", to: "/quick-payment" },
                { name: "Registration Policy", to: "/registration-policy" },
                { name: "Cancellation Policy", to: "/cancel" },
                { name: "Careers", to: "/careers" },
                { name: "Contact Us", to: "/contact" },
                { name: "Franchise", to: "/franchise" },
                { name: "Free", to: "/free" },
                { name: "Students", to: "/students" },
                { name: "Training Centers", to: "/training" },
              ].map((item) => (
                <Link
                  key={item.name}
                  to={item.to}
                  className={`px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-[#06b6d4] hover:to-[#7c3aed] transition-all duration-300 border border-gray-600 rounded-lg hover:border-[#06b6d4] hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] relative overflow-hidden group transform hover:scale-105 hover:-translate-y-1 ${
                    item.name === "Registration Policy"
                      ? "bg-gradient-to-r from-[#06b6d4] to-[#7c3aed] border-[#06b6d4] shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                      : ""
                  }`}
                >
                  <span className="relative z-10">{item.name}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/20 to-[#7c3aed]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#06b6d4] group-hover:w-full transition-all duration-300"></div>
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#06b6d4] to-[#7c3aed] opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                </Link>
              ))}
            </div>
            {/* Bottom Section */}
            <div className="border-t border-gray-700 pt-8">
              <div className="text-center">
                <p className="text-sm text-gray-400">
                  © 2016 StructuralGuru. All Rights Reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="group bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-3xl hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(6,182,212,0.6)]"
        >
          {isChatOpen ? (
            <X className="w-6 h-6 transition-transform duration-300 group-hover:rotate-90" />
          ) : (
            <MessageCircle className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
          )}
        </button>
      </div>

      {/* Chat Box */}
      <div
        className={`fixed bottom-24 right-6 w-80 bg-white/95 backdrop-blur-xl border border-gray-200/20 rounded-3xl shadow-2xl transition-all duration-500 z-40 ${
          isChatOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 translate-y-8 invisible"
        }`}
      >
        <div className="p-6">
          <h3 className="text-xl font-semibold text-[#0f766e] mb-6 text-center">Customer Support</h3>
          <form className="space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300"
              />
            </div>
            <div className="relative">
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300"
              />
            </div>
            <div className="relative">
              <textarea
                placeholder="How can I help you?"
                rows={3}
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 resize-none"
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white py-3 rounded-2xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
            >
              Customer Care for Support
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}


function PolicySection({ title, items, content }) {
  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold mb-4 transition-all duration-300">
        <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">{title}</span>
      </h2>
      {items && (
        <ul className="list-disc list-inside mb-4 space-y-2">
          {items.map((item, index) => (
            <li
              key={index}
              className="text-gray-700 leading-relaxed transition-colors duration-300 hover:text-gray-800"
            >
              {item}
            </li>
          ))}
        </ul>
      )}
      {content && content}
    </div>
  )
}
